import { motion, AnimatePresence } from 'motion/react';
import { Link, useRouterState, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useAdminStatus } from '@/hooks/useAdminStatus';
import { usePendingQuizzes } from '@/hooks/usePendingQuizzes';
import { useUserTier } from '@/queries/subscriptions';
import styles from './Sidebar.module.scss';
import { apiConfig } from '@/config/api';

// Icons
import BookCheckIcon from '@/assets/common/book-check.svg?react';
import BrainCircuitIcon from '@/assets/common/brain-circuit.svg?react';
import ChartScatterIcon from '@/assets/common/chart-scatter.svg?react';
import ChartSplineIcon from '@/assets/common/chart-spline.svg?react';
import CreatePencilRuleIcon from '@/assets/common/create-pencil-ruler.svg?react';
import EyeIcon from '@/assets/common/eye.svg?react';
import GemIcon from '@/assets/common/gem.svg?react';
import LayoutDashboardIcon from '@/assets/common/layout-dashboard.svg?react';
import PickaxeIcon from '@/assets/common/pickaxe.svg?react';
import RobotFaceIcon from '@/assets/common/robot-face.svg?react';
import SlidersHorizontalIcon from '@/assets/common/sliders-horizontal.svg?react';
import TelescopeIcon from '@/assets/common/telescope.svg?react';
import TentTreeIcon from '@/assets/common/tent-tree.svg?react';
import UserPenIcon from '@/assets/common/user-pen.svg?react';
import SproutIcon from '@/assets/common/sprout.svg?react';
import SquarePenIcon from '@/assets/common/square-pen.svg?react';
import FolderIcon from '@/assets/common/folder.svg?react';
import LibraryIcon from '@/assets/common/library.svg?react';

export function Sidebar() {
    const { signOut } = useAuth();
    const { isAdmin } = useAdminStatus();
    const { counts } = usePendingQuizzes();
    const userTier = useUserTier();
    const hasCollectionsAccess = userTier !== 'foundation';
    const routerState = useRouterState();
    const currentPath = routerState.location.pathname;
    const navigate = useNavigate();

    const [expandedSections, setExpandedSections] = useState({
        create: false,
        learn: false,
        test: false,
        analytics: false,
    });

    const toggleSection = (section: 'create' | 'learn' | 'test' | 'analytics') => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const isActive = (path: string) => {
        if (path === '/') return currentPath === '/';
        return currentPath.startsWith(path);
    };

    const handleSignOut = async () => {
        await signOut();
        navigate({ to: '/login' });
    };

    return (
        <div className={styles.sidebar}>
            <nav className={styles.sidebar__nav}>
                <div className={styles.sidebar__inner}>
                {/* CREATE SECTION */}
                <div className={styles.sidebar__section}>
                    <button
                        className={`${styles.sidebar__link} ${styles['sidebar__link--purple']} ${(isActive('/') || isActive('/quizlets/create') || isActive('/review-center')) && !isActive('/quizlets') && !isActive('/daily-knowledge') && !isActive('/pricing') ? styles['sidebar__link--active'] : ''
                            }`}
                        onClick={() => toggleSection('create')}
                    >
                        <span className={styles.sidebar__linkIcon}><TelescopeIcon /></span>
                        Create
                        <span
                            className={styles.sidebar__arrow}
                            style={{ transform: expandedSections.create ? 'rotate(180deg)' : 'rotate(0deg)' }}
                        >
                            ▾
                        </span>
                    </button>
                    <AnimatePresence>
                        {expandedSections.create && (
                            <motion.div
                                className={styles.sidebar__collapse}
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                            >
                                <Link
                                    to="/"
                                    className={`${styles.sidebar__subLink} ${styles['sidebar__subLink--purple']} ${isActive('/') && !isActive('/quizlets/create') ? styles['sidebar__subLink--active'] : ''}`}
                                >
                                    <span><RobotFaceIcon /></span> Generate (AI)
                                </Link>
                                <Link
                                    to="/quizlets/create"
                                    className={`${styles.sidebar__subLink} ${styles['sidebar__subLink--purple']} ${isActive('/quizlets/create') ? styles['sidebar__subLink--active'] : ''}`}
                                >
                                    <span><PickaxeIcon /></span> Manual
                                </Link>
                                <Link
                                    to="/review-center"
                                    className={`${styles.sidebar__subLink} ${styles['sidebar__subLink--purple']} ${isActive('/review-center') ? styles['sidebar__subLink--active'] : ''}`}
                                >
                                    <span><EyeIcon /></span> Review Center
                                    {counts.total > 0 && (
                                        <span className={styles.sidebar__badge}>{counts.total}</span>
                                    )}
                                </Link>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* LEARN SECTION */}
                {hasCollectionsAccess && (
                    <div className={styles.sidebar__section}>
                        <button
                            className={`${styles.sidebar__link} ${styles['sidebar__link--blue']} ${(isActive('/articles')) ? styles['sidebar__link--active'] : ''
                                }`}
                            onClick={() => toggleSection('learn')}
                        >
                            <span className={styles.sidebar__linkIcon}><LibraryIcon /></span>
                            Learn
                            <span
                                className={styles.sidebar__arrow}
                                style={{ transform: expandedSections.learn ? 'rotate(180deg)' : 'rotate(0deg)' }}
                            >
                                ▾
                            </span>
                        </button>
                        <AnimatePresence>
                            {expandedSections.learn && (
                                <motion.div
                                    className={styles.sidebar__collapse}
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                >
                                    <Link
                                        to="/articles"
                                        className={`${styles.sidebar__subLink} ${styles['sidebar__subLink--blue']} ${isActive('/articles') && !isActive('/articles/collections') ? styles['sidebar__subLink--active'] : ''}`}
                                    >
                                        <span><SquarePenIcon /></span> Articles
                                    </Link>
                                    <Link
                                        to={'/articles/collections' as string}
                                        className={`${styles.sidebar__subLink} ${styles['sidebar__subLink--blue']} ${isActive('/articles/collections') ? styles['sidebar__subLink--active'] : ''}`}
                                    >
                                        <span><FolderIcon /></span> Collections
                                    </Link>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                )}

                {/* TEST SECTION */}
                <div className={styles.sidebar__section}>
                    <button
                        className={`${styles.sidebar__link} ${styles['sidebar__link--green']} ${(isActive('/daily-knowledge') || isActive('/quizlets')) ? styles['sidebar__link--active'] : ''
                            }`}
                        onClick={() => toggleSection('test')}
                    >
                        <span className={styles.sidebar__linkIcon}><BookCheckIcon /></span>
                        Test
                        <span
                            className={styles.sidebar__arrow}
                            style={{ transform: expandedSections.test ? 'rotate(180deg)' : 'rotate(0deg)' }}
                        >
                            ▾
                        </span>
                    </button>
                    <AnimatePresence>
                        {expandedSections.test && (
                            <motion.div
                                className={styles.sidebar__collapse}
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                            >
                                <Link
                                    to="/daily-knowledge"
                                    className={`${styles.sidebar__subLink} ${styles['sidebar__subLink--green']} ${isActive('/daily-knowledge') ? styles['sidebar__subLink--active'] : ''}`}
                                >
                                    <span><BrainCircuitIcon /></span> Daily Review
                                </Link>
                                <Link
                                    to="/quizlets"
                                    className={`${styles.sidebar__subLink} ${styles['sidebar__subLink--green']} ${isActive('/quizlets') && !isActive('/quizlets/create') ? styles['sidebar__subLink--active'] : ''}`}
                                >
                                    <span><CreatePencilRuleIcon /></span> Decks
                                </Link>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* ANALYTICS */}
                <div className={styles.sidebar__section}>
                    <button
                        className={`${styles.sidebar__link} ${styles['sidebar__link--orange']} ${isActive('/analytics') ? styles['sidebar__link--active'] : ''
                            }`}
                        onClick={() => toggleSection('analytics')}
                    >
                        <span className={styles.sidebar__linkIcon}><ChartSplineIcon /></span>
                        Analytics
                        <span
                            className={styles.sidebar__arrow}
                            style={{ transform: expandedSections.analytics ? 'rotate(180deg)' : 'rotate(0deg)' }}
                        >
                            ▾
                        </span>
                    </button>
                    <AnimatePresence>
                        {expandedSections.analytics && (
                            <motion.div
                                className={styles.sidebar__collapse}
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                            >
                                <Link
                                    to="/analytics"
                                    search={{ timeRange: 'last30', isDailyKnowledge: false }}
                                    className={`${styles.sidebar__subLink} ${styles['sidebar__subLink--orange']} ${isActive('/analytics') && !isActive('/analytics/quizlets') ? styles['sidebar__subLink--active'] : ''}`}
                                >
                                    <span><LayoutDashboardIcon /></span> Dashboard
                                </Link>
                                {isAdmin && (
                                    <Link
                                        to="/analytics/quizlets"
                                        className={`${styles.sidebar__subLink} ${styles['sidebar__subLink--orange']} ${isActive('/analytics/quizlets') ? styles['sidebar__subLink--active'] : ''}`}
                                    >
                                        <span><BookCheckIcon /></span> By Quizlet
                                    </Link>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* PRICING */}
                <Link
                    to="/pricing"
                    className={`${styles.sidebar__link} ${styles['sidebar__link--pink']} ${isActive('/pricing') ? styles['sidebar__link--active'] : ''
                        }`}
                >
                    <span className={styles.sidebar__linkIcon}><GemIcon /></span>
                    Pricing
                </Link>

                {/* ABOUT */}
                <Link
                    to="/about"
                    className={`${styles.sidebar__link} ${styles['sidebar__link--teal']} ${isActive('/about') ? styles['sidebar__link--active'] : ''}`}
                >
                    <span className={styles.sidebar__linkIcon}><SproutIcon /></span>
                    About
                </Link>

                <div className={styles.sidebar__divider} />

                {/* ACCOUNT SECTION */}
                {apiConfig.getEnvironment() !== 'production' && (
                    <Link
                        to="/account"
                        className={`${styles.sidebar__link} ${styles['sidebar__link--gray']}`}
                    >
                        <span className={styles.sidebar__linkIcon}><SlidersHorizontalIcon /></span>
                        Settings
                    </Link>
                )}

                <Link
                    to="/account/usage"
                    className={`${styles.sidebar__link} ${styles['sidebar__link--gray']}`}
                >
                    <span className={styles.sidebar__linkIcon}><ChartScatterIcon /></span>
                    Usage
                </Link>

                {isAdmin && (
                    <Link
                        to="/admin/users"
                        className={`${styles.sidebar__link} ${styles['sidebar__link--gray']}`}
                    >
                        <span className={styles.sidebar__linkIcon}><UserPenIcon /></span>
                        User Management
                    </Link>
                )}

                <div className={styles.sidebar__divider} />

                <button
                    className={`${styles.sidebar__link} ${styles['sidebar__link--red']}`}
                    onClick={handleSignOut}
                >
                    <span className={styles.sidebar__linkIcon}><TentTreeIcon /></span>
                    Log Out
                </button>
                </div>
            </nav>
        </div>
    );
}
